import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DollarSign, Users, TrendingUp, Award, CheckCircle } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

const Affiliate = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: ""
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    toast.success("Application submitted! We'll contact you soon.");
    setFormData({ name: "", email: "", phone: "", company: "", message: "" });
  };

  const benefits = [
    {
      icon: <DollarSign className="w-8 h-8 text-primary" />,
      title: "Competitive Commission",
      description: "Earn generous commissions on every successful referral"
    },
    {
      icon: <Users className="w-8 h-8 text-primary" />,
      title: "Marketing Support",
      description: "Access to marketing materials and sales resources"
    },
    {
      icon: <TrendingUp className="w-8 h-8 text-primary" />,
      title: "Recurring Revenue",
      description: "Build passive income with ongoing client relationships"
    },
    {
      icon: <Award className="w-8 h-8 text-primary" />,
      title: "Training & Support",
      description: "Comprehensive training on our products and services"
    }
  ];

  const perks = [
    "High commission rates on all sales",
    "Dedicated affiliate support team",
    "Real-time tracking dashboard",
    "Monthly bonus opportunities",
    "Exclusive partner events",
    "Early access to new products"
  ];

  return (
    <>
      <SEO 
        title="Affiliate Program - Neil AI Global Solutions"
        description="Join the Neil AI Global Solutions affiliate program and earn competitive commissions by promoting cutting-edge AI and robotics solutions."
        keywords="affiliate program, partner program, reseller, AI solutions affiliate, robotics partner"
      />
      
      <div className="min-h-screen pt-24 pb-16">
        <div className="container mx-auto px-4 sm:px-6">
          {/* Hero Section */}
          <div className="text-center mb-12 sm:mb-16">
            <h1 className="font-orbitron text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 leading-tight">
              <span className="text-primary">Affiliate</span> Partner Program
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Join our growing network of partners and earn competitive commissions by promoting industry-leading AI automation and robotics solutions
            </p>
          </div>

          {/* Benefits Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12 sm:mb-16">
            {benefits.map((benefit, index) => (
              <Card key={index} className="glass-effect border-primary/20">
                <CardHeader>
                  <div className="mb-4">{benefit.icon}</div>
                  <CardTitle className="text-lg sm:text-xl">{benefit.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-sm sm:text-base">
                    {benefit.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-start">
            {/* Why Join Section */}
            <Card className="glass-effect border-primary/20">
              <CardHeader>
                <CardTitle className="font-orbitron text-2xl sm:text-3xl">
                  Why Join Our Program?
                </CardTitle>
                <CardDescription className="text-base sm:text-lg">
                  Partner with a leader in AI automation
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {perks.map((perk, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <p className="text-sm sm:text-base text-muted-foreground">{perk}</p>
                  </div>
                ))}
                
                <div className="mt-8 p-4 sm:p-6 bg-primary/10 rounded-lg border border-primary/20">
                  <h4 className="font-bold text-lg sm:text-xl mb-2">Commission Structure</h4>
                  <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                    Earn up to 15% commission on initial sales and 10% recurring commission on service contracts. The more you sell, the more you earn!
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Application Form */}
            <Card className="glass-effect border-primary/20">
              <CardHeader>
                <CardTitle className="font-orbitron text-2xl sm:text-3xl">
                  Apply Now
                </CardTitle>
                <CardDescription className="text-base sm:text-lg">
                  Start your journey as an affiliate partner
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      className="bg-background/50"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      className="bg-background/50"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      required
                      className="bg-background/50"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="company">Company Name</Label>
                    <Input
                      id="company"
                      value={formData.company}
                      onChange={(e) => setFormData({...formData, company: e.target.value})}
                      className="bg-background/50"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="message">Why do you want to become an affiliate?</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      rows={4}
                      className="bg-background/50"
                    />
                  </div>

                  <Button type="submit" className="w-full" size="lg">
                    Submit Application
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* CTA Section */}
          <div className="mt-12 sm:mt-16 text-center">
            <Card className="glass-effect border-primary/20 max-w-3xl mx-auto">
              <CardContent className="p-6 sm:p-8">
                <h3 className="font-orbitron text-xl sm:text-2xl font-bold mb-4">
                  Questions About Our Program?
                </h3>
                <p className="text-muted-foreground mb-6 text-sm sm:text-base leading-relaxed">
                  Our affiliate team is here to help you succeed. Contact us for more information about commission rates, marketing materials, and support.
                </p>
                <Button asChild size="lg" variant="outline">
                  <a href="mailto:affiliates@neilaiglobalsolutionsllc.com">
                    Contact Affiliate Team
                  </a>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default Affiliate;
